/**
 * Reading the attack's participants out of state: the attacker/target lookups every
 * resolution entry point starts from.
 */

import type { Cell, Combatant, EncounterState } from "@lancer-vtt/domain";
import { cellAsSquare, isCharacter, squareAnchor } from "@lancer-vtt/domain";
import { squareFootprint, squareKey } from "@lancer-vtt/space";
import type { AttackTarget } from "@lancer-vtt/shared";
import { ResolveError } from "./types";

/** Read a combatant or throw a {@link ResolveError}. */
export function requireCombatant(state: EncounterState, id: string): Combatant {
  const c = state.combatants[id];
  if (!c) throw new ResolveError(`resolve: unknown combatant ${id}`);
  return c;
}

/**
 * The combatant occupying a targeted square cell (footprint-covering), or null if
 * the cell is empty — the resolution target of a `{ kind: "cell" }` attack
 * (`mech-destruction` Phase 1, D8, `decisions/0064`).
 *
 * **Deterministic character-over-object overlap preference.** As of the
 * passable-object movement seam, a mech/NPC/pilot can legally stand on the SAME
 * cell as a passable object/deployable (a wreck, smoke) — the first legitimate
 * two-tokens-one-cell case. Iteration order over `state.combatants` (a plain
 * object) is otherwise insertion-order-ish but not a rule the resolver should
 * depend on, so this picks the first **character**-kind occupant it finds
 * (`isCharacter`, capabilities-over-kinds Phase 1 — object/deployable is exactly
 * its complement over the kind set, so this reads identically to the former local
 * `isObjectKind` negation) and only falls back to an object/deployable occupant
 * when no character overlaps the cell — the object stays targetable by an
 * explicit `{ kind: "unit" }` id (unaffected by this function) or by cell-click
 * when it's alone there.
 *
 * **Composes with the fog-of-war concealment gate for free.** This function
 * reads no `concealed` field and emits no message — it is blind to concealment,
 * the same way the pre-existing "first map-order match" behavior was. So a
 * concealed mech standing on a visible wreck resolves to the (concealed) mech
 * exactly as a visible one would, and no reject/log this function produces ever
 * differentiates the two cases — there is nothing here for a concealed occupant
 * to leak through (fog's shipped convention: rejects never name a concealed
 * blocker — `backend/src/fogOfWarWire.integration.test.ts`).
 */
function combatantAtCell(state: EncounterState, cell: Cell): Combatant | null {
  const sq = cellAsSquare(cell);
  if (!sq) return null;
  const key = squareKey(sq);
  let objectMatch: Combatant | null = null;
  for (const c of Object.values(state.combatants)) {
    const anchor = squareAnchor(c);
    if (!anchor) continue;
    for (const f of squareFootprint(anchor, c.base.size)) {
      if (squareKey(f) !== key) continue;
      if (isCharacter(c)) return c; // a character occupant wins immediately — deterministic.
      if (!objectMatch) objectMatch = c; // remember the first object match in case no character overlaps.
      break;
    }
  }
  return objectMatch;
}

/**
 * The **primary target combatant** an attack resolves its to-hit roll against — the unit a
 * `{ kind: "unit" }` target names, or the occupant of a `{ kind: "cell" }` target's cell. This
 * is the single-target spine; the AoE fan-out (Phase 3) layers *additional* affected combatants
 * around it from the weapon's template. A cell with no occupant throws — there is nothing to roll
 * a to-hit against (an AoE that catches only empty ground is a Phase-3 concern, handled there).
 */
export function resolvePrimaryTarget(state: EncounterState, target: AttackTarget): Combatant {
  if (target.kind === "unit") return requireCombatant(state, target.id);
  const occupant = combatantAtCell(state, target.cell as Cell);
  if (!occupant) throw new ResolveError(`resolve: no combatant at targeted cell`);
  return occupant;
}
